import ajax from "./ajax";

/**
 * Accounts onboarding wizard API client.
 *
 * Hits the new `/accounts/*` and extended `/onboarding/*` endpoints on
 * riverr-backend-node. All endpoints except `registerAccountsUser` and
 * `checkSubdomain` require an authenticated user (Firebase ID token via
 * the existing `x-uid` header set by `ajax.js`).
 *
 * Persistence model: the server keeps a single `onboardings/{uid}` doc
 * with sparse per-step blocks (`business`, `production`, `sales`,
 * `branding`). Each PUT replaces its own block; nothing else is
 * touched. `finalize` is the atomic enterprise-creation step — it
 * reads the assembled record, creates the Enterprise + theme, and
 * links the user.
 *
 * NOTE on path naming: the directive's reference names these
 * `/accounts/onboarding/step`, `/accounts/subdomain-available`,
 * `/accounts/onboarding/finalize`. The actual routes ship under
 * `/onboarding/*` instead — same logic, just keeps the wizard
 * endpoints in one router rather than splitting them across two
 * namespaces. (The /accounts namespace still exists for
 * `/accounts/register`, which is genuinely a separate concern.)
 */

/**
 * POST /accounts/register — founder signup (creates Firebase Auth user
 * with NO enterprise attached). Distinct from `/auth/signup` which
 * attaches a user to an existing enterprise via the hostname lookup.
 *
 * Payload:
 *   { email, password }                       — email/password flow
 *   { email, uid, displayName?, photoURL? }   — Google flow (uid from popup)
 *
 * Returns: { customToken, uid, user }
 */
export function registerAccountsUser(data) {
  return ajax("/accounts/register", { method: "POST", data });
}

/** POST /onboarding/init — idempotent; creates the user's onboarding doc. */
export function initOnboarding() {
  return ajax("/onboarding/init", { method: "POST" });
}

/** GET /onboarding/state — current step + persisted answers. */
export function getOnboardingState() {
  return ajax("/onboarding/state");
}

/** PUT /onboarding/business — persists Step 2 answers. */
export function saveBusinessProfile(data) {
  return ajax("/onboarding/business", { method: "PUT", data });
}

/**
 * POST /onboarding/enrich-website — best-effort enrichment from a website.
 * Returns { enrichment: { businessName, city, state, logo:{url,id},
 * brandColor, brandColors[], description, aboutText, missionSummary,
 * heroImage } }. Used by founder onboarding and Themes → Generate About seeder.
 */
export function enrichWebsite(url) {
  return ajax("/onboarding/enrich-website", { method: "POST", data: { url } });
}

/**
 * POST /onboarding/migrate-store — scrape branding + content pages from any
 * existing website. Preview only; apply happens in shop settings.
 * Returns { enrichment, home, pages, nav }.
 */
export function migrateStore(url) {
  return ajax("/onboarding/migrate-store", {
    method: "POST",
    data: { url },
    timeout: 180000,
  });
}

/**
 * POST /onboarding/migrate-store-products — create store listings from a
 * migrate-store preview. Each color keeps the photos linked to it.
 */
export function importMigratedProducts(data) {
  return ajax("/onboarding/migrate-store-products", {
    method: "POST",
    data,
    timeout: 120000,
  });
}

/**
 * POST /onboarding/migrate-store-polish — rewrite scraped homepage + page
 * copy with the store-builder voice model. Fail-soft.
 * Payload: { storeName?, home?: { title, subtitle }, pages: [...] }
 * Returns: { home, pages, usedAi, remaining? }
 */
export function polishMigratedStore(data) {
  return ajax("/onboarding/migrate-store-polish", {
    method: "POST",
    data,
    timeout: 35000,
  });
}

/**
 * POST /onboarding/migrate-store-visual — vision layout match for underbase
 * variants + full token overrides. Fail-soft.
 * Returns { visualAnalysis, usedAi, remaining? }
 */
export function analyzeMigratedStore(url) {
  return ajax("/onboarding/migrate-store-visual", {
    method: "POST",
    data: { url },
    timeout: 25000,
  });
}

/** PUT /onboarding/production — persists Step 3 answers (decoration / model / volume). */
export function saveProduction(data) {
  return ajax("/onboarding/production", { method: "PUT", data });
}

/** PUT /onboarding/sales — persists Step 4 answers (channels / customers / current tool). */
export function saveSales(data) {
  return ajax("/onboarding/sales", { method: "PUT", data });
}

/**
 * GET /onboarding/check-subdomain?subdomain=foo
 * Returns: { available: bool, reason?: 'taken' | 'reserved' | 'invalid' }
 */
export function checkSubdomain(subdomain) {
  return ajax("/onboarding/check-subdomain", { query: { subdomain } });
}

/**
 * POST /onboarding/finalize — atomic enterprise creation + theme save.
 *
 * Payload: { subdomain, logo: {url,id}, brandColor, logoSymbol?: {url,id} }
 * Returns: { enterpriseId, tenantUrl }
 *
 * Backend does NOT set `signupSource` so the existing
 * `isLockedToPricingSelector` stays dormant — see plan doc + pricing
 * directive. When pricing eventually ships revenue-action gates, it
 * will introduce its own signal rather than overloading signupSource.
 */
export function finalizeOnboarding(data) {
  return ajax("/onboarding/finalize", { method: "POST", data });
}

/**
 * PUT /onboarding/branding — URL/branding step. Creates the DRAFT enterprise
 * (subdomain + theme) on first call. Payload:
 *   { subdomain, logo: {url,id}, brandColor, logoSymbol?: {url,id} }
 * Returns: { enterpriseId, hostname, currentStep }
 */
export function saveOnboardingBranding(data) {
  return ajax("/onboarding/branding", { method: "PUT", data });
}

/** PUT /onboarding/pricing — record selected pricing table ids. */
export function saveOnboardingPricing(data) {
  return ajax("/onboarding/pricing", { method: "PUT", data });
}

/** PUT /onboarding/products — record + seed selected master product ids. */
export function saveOnboardingProducts(data) {
  return ajax("/onboarding/products", { method: "PUT", data });
}

/** PUT /onboarding/print-options — per-category placements + method bindings. */
export function saveOnboardingPrintOptions(data) {
  return ajax("/onboarding/print-options", { method: "PUT", data });
}

/** PUT /onboarding/store — record the store choice (build-my-store flow). */
export function saveOnboardingStore(data) {
  return ajax("/onboarding/store", { method: "PUT", data });
}

/**
 * POST /onboarding/store-products — auto-publish blank products onto a
 * freshly-created company store as buyer-customizable listings.
 * Payload: { shopId, blankProductIds?: string[], categoryImages? }
 * When blankProductIds is omitted, uses the founder's onboarding selections.
 * Best-effort: callers should not block store creation on its result.
 */
export function materializeOnboardingStoreProducts(data) {
  return ajax("/onboarding/store-products", { method: "POST", data });
}

/**
 * POST /onboarding/publish-categories — idempotent pass that ensures every
 * blank already seeded onto a shop has its categories written to Firestore.
 * Call this after catalog seeding so the storefront category grid is always
 * populated when the store goes live.
 *
 * Payload: { shopId, categoryImages?: { [ghostCategoryId | groupName]: url } }
 * Returns: { categories: number }
 */
export function publishStoreCategories(data) {
  return ajax("/onboarding/publish-categories", { method: "POST", data });
}

/**
 * GET /blank-products/masters — the global master catalog for the onboarding
 * product picker. Falls back gracefully if the endpoint shape differs.
 */
export function getOnboardingMasters() {
  return ajax("/blank-products/masters");
}

// ── Store-builder wizard (brand / imagery / merch-drop) ──────────────

/**
 * POST /onboarding/hero-image — AI hero image candidates.
 * Payload: { storeName, businessType?, brandColors?: [hex], stylePrompt?, numImages? }
 * Returns: { images: [{ id, url }], remaining }
 */
export function generateStoreHeroImage(data) {
  return ajax("/onboarding/hero-image", { method: "POST", data });
}

/**
 * POST /onboarding/hero-from-mockups — group hero scene from the seller's
 * actual composited merch mockups (same idea as lifestyle tiles).
 * Payload: {
 *   mockups: [{ mockupBase64, name? }],
 *   storeName?, businessType?, brandColors?, stylePrompt?,
 *   numImages?, wizardSessionId?,
 * }
 * Returns: { images: [{ id, url }], remaining }
 */
export function generateHeroFromMockups(data) {
  return ajax("/onboarding/hero-from-mockups", { method: "POST", data });
}

/**
 * POST /onboarding/company-catalog-hero — studio product collage for company
 * stores (Your Design Here on real blanks; no people).
 * Payload: {
 *   mockups: [{ mockupBase64, name? }],
 *   storeName?, businessType?, brandColors?, stylePrompt?,
 *   numImages?, wizardSessionId?,
 * }
 * Returns: { images: [{ id, url }], remaining }
 */
export function generateCompanyCatalogHero(data) {
  return ajax("/onboarding/company-catalog-hero", { method: "POST", data });
}

/**
 * POST /onboarding/category-tiles — branded AI category tile images.
 * Payload: { categories: [{ id, name }], brandColors?: [hex] }
 * Returns: { tiles: [{ categoryId, name, url }], remaining }
 */
export function generateStoreCategoryTiles(data) {
  return ajax("/onboarding/category-tiles", { method: "POST", data });
}

/**
 * POST /onboarding/category-lifestyle-tiles — lifestyle category tiles from
 * the seller's ACTUAL merch (nano banana image-to-image over client-side
 * composited mockups).
 * Payload: { tiles: [{ categoryId, name, mockupBase64, stylePrompt? }], brandColors?, wizardSessionId? }
 * Returns: { tiles: [{ categoryId, name, url }], remaining }
 */
export function generateCategoryLifestyleTiles(data) {
  return ajax("/onboarding/category-lifestyle-tiles", { method: "POST", data });
}

/**
 * GET /onboarding/category-tile-defaults — shared pre-generated category tile
 * images (clean blank garments, no prints) keyed by ghostCategoryId + group.
 * Returns: { tiles: { [ghostCategoryId]: url }, groups: { [group]: url } }
 * Images are master-catalog lifestyle mockups (lifestyle/{style}/primary.png).
 */
export function getCategoryTileDefaults() {
  return ajax("/onboarding/category-tile-defaults");
}

/**
 * POST /onboarding/merch-designs — transparent print-ready design candidates
 * for the merch-drop seeder.
 * Payload: {
 *   prompt, numImages?, brandColors?: [hex],
 *   referenceImageBase64?, referenceImageUrl?, wizardSessionId?, creditBacked?
 * }
 * Returns: { designs: [{ id, url }], remaining }
 */
export function generateMerchDesigns(data) {
  return ajax("/onboarding/merch-designs", { method: "POST", data });
}

/**
 * POST /onboarding/store-logo — Nano Banana storefront logo.
 * Payload: { storeName?, prompt?, brandColors?: [hex], wizardSessionId? }
 * Returns: { logo: { id, url }, remaining }
 */
export function generateStoreLogo(data) {
  return ajax("/onboarding/store-logo", { method: "POST", data, timeout: 120000 });
}

/**
 * POST /onboarding/merch-design-concepts — free text concepts for the
 * collection approval board. Credits are charged only when designs generate.
 * Payload: {
 *   storeName?, teamName?, occasion?, notes?, collectionType?,
 *   brandColors?, numConcepts?, avoidTitles?, wizardSessionId?
 * }
 * Returns: { concepts: [{ id, title, style, description }], remaining }
 */
export function generateMerchDesignConcepts(data) {
  return ajax("/onboarding/merch-design-concepts", { method: "POST", data });
}

/**
 * POST /onboarding/store-plan — propose multiple merch collections.
 * Payload: { storeName?, brandSummary?, audience?, notes?, brandColors?, wizardSessionId? }
 * Returns: { collections: [{ id, name, collectionType, occasion, notes, summary }], remaining }
 */
export function generateStorePlan(data) {
  return ajax("/onboarding/store-plan", { method: "POST", data });
}

/** GET /onboarding/store-builder-ai-usage — remaining AI budget per kind. */
export function getStoreBuilderAiUsage() {
  return ajax("/onboarding/store-builder-ai-usage");
}

/**
 * POST /onboarding/brand-voice — interactive AI copywriter for the store
 * builder. Send the full chat each turn (stateless server).
 * Payload: {
 *   storeName,
 *   targets: [{ key, label, default?, maxLength? }],
 *   messages: [{ role: "user"|"assistant", content }],
 * }
 * Returns: { type: "question"|"copy", message, profile?, copy?, raw, remaining }
 * `raw` must be echoed back as the assistant message content next turn.
 */
export function brandVoiceChat(data) {
  return ajax("/onboarding/brand-voice", {
    method: "POST",
    data,
    timeout: 60000,
  });
}

/** POST /onboarding/compose-theme — one theme-builder turn (reply + analysis). */
export function composeThemeTurn(data) {
  return ajax("/onboarding/compose-theme", { method: "POST", data, timeout: 180000 });
}

/**
 * POST /onboarding/merch-drop — seed designed products (design × blank) onto
 * a freshly created store.
 * Payload: { shopId, designs: [{ id, url, name? }], blankProductIds: [] }
 * Returns: { jobId, total }
 */
export function startMerchDrop(data) {
  return ajax("/onboarding/merch-drop", { method: "POST", data });
}

/** POST /onboarding/bulk-create-draft — account copy of the bulk-create setup. */
export function saveBulkCreateDraft(key, draft, savedAt) {
  return ajax("/onboarding/bulk-create-draft", {
    method: "POST",
    data: { key, draft, savedAt },
  });
}

/** GET /onboarding/bulk-create-draft?key= */
export function getBulkCreateDraft(key) {
  return ajax("/onboarding/bulk-create-draft", { query: { key } });
}

/** GET /onboarding/merch-drop-status?shopId= — merch-drop job progress. */
export function getMerchDropStatus(shopId) {
  return ajax("/onboarding/merch-drop-status", { query: { shopId } });
}

/**
 * POST /onboarding/merch-drop-preview — per-blank data for REAL product
 * previews & the review page: placement geometry, mockup canvas, retail
 * price, and per-color photo/hex/kept-by-brand info.
 * Payload: { blankProductIds: [], brandColors?: [hex] }
 * Returns: { blanks: [{ blankId, name, placementId, printSizes, mockupDims,
 *   price, colors: [{ color, colorCode, frontUrl, kept, isDefault }], sizes }] }
 */
export function getMerchDropPreview(data) {
  return ajax("/onboarding/merch-drop-preview", { method: "POST", data });
}

/**
 * POST /onboarding/merch-drop-brief — AI placement brief → structured rules.
 * Payload: { brief, designs: [{id,name,url}], categories?: [{id,label}] }
 * Returns: { plan: { summary, style, nameHints, maxBlanksPerCategory, rules } }
 */
export function interpretMerchDropBrief(data) {
  return ajax("/onboarding/merch-drop-brief", { method: "POST", data });
}

/**
 * POST /onboarding/remove-background — strip the background from a design
 * image using Ideogram's remove-background model. Accepts a base64 PNG
 * (with or without the `data:` prefix) and returns a transparent-background
 * base64 PNG — used by the wizard Design Studio's BG Remover toolbar button.
 * Payload: { imageBase64: string }
 * Returns: { base64: string }
 */
export function removeDesignBackground(data) {
  return ajax("/onboarding/remove-background", { method: "POST", data });
}

/**
 * POST /freepik/ai/upscale — generative (Magnific) upscale of a design image.
 * Same endpoint the storefront customizer's Upscale tool uses.
 * Payload: { image: base64/dataURL, scaleFactor: "2x"|"4x", creativity: 0..1, recut?: bool }
 * Returns: { data: [{ base64, skipped?, degraded? }] }
 */
export function upscaleDesignImage(data) {
  return ajax("/freepik/ai/upscale", { method: "POST", data });
}

/**
 * POST /ai/pricing-csv-mapping — propose a column→field mapping for a pricing
 * CSV the deterministic header auto-map couldn't fully resolve.
 *
 * Payload: { method, headers: [], sampleRows: [[]], allowedFields: [] }
 * Returns: { enabled: bool, mapping: { [header]: fieldKey } }
 *
 * `enabled:false` means no model is configured server-side — the dialog then
 * falls back to manual mapping. AI proposes only; the human still confirms.
 */
export function proposePricingCsvMapping(data) {
  return ajax("/ai/pricing-csv-mapping", { method: "POST", data });
}

/**
 * GET /users/token — mints a fresh Firebase custom token for the
 * currently-authenticated user. Used after finalize to re-authenticate
 * the founder on their new `{subdomain}.myriverr.com` domain (Firebase
 * sessions are origin-scoped, so the accounts.riverr.app session does
 * not carry over to the tenant subdomain).
 *
 * Returns: { token }
 */
export function getSignInToken() {
  return ajax("/users/token");
}
